import React, { useEffect, useState, useContext } from "react";
import { UserContext } from "./UserContext";
import { useTheme } from "./ThemeContext";
import { Check, X } from 'lucide-react';

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL;

function AdminPanel() {
  const { userInfo } = useContext(UserContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionMsg, setActionMsg] = useState("");
  const { theme } = useTheme();

  useEffect(() => {
    if (userInfo && userInfo.email === ADMIN_EMAIL) {
      fetchUsers();
    }
  }, [userInfo]);

  async function fetchUsers() {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/users`, { credentials: "include" });
      if (!res.ok) {
        const data = await res.json();
        setError(data.error || "Access denied");
        setUsers([]);
        setLoading(false);
        return;
      }
      const data = await res.json();
      setUsers(data);
    } catch (err) {
      setError("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }

  async function handleAction(id, action) {
    setActionMsg("");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/users/${id}/${action}`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (res.ok) {
        setActionMsg(data.message);
        fetchUsers();
      } else {
        setActionMsg(data.error || "Action failed");
      }
    } catch (err) {
      setActionMsg("Action failed");
    }
  }

  const bgColor = theme === 'dark' ? 'bg-gray-800' : 'bg-white';
  const textColor = theme === 'dark' ? 'text-white' : 'text-gray-800';
  const tableHeaderBg = theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100';
  const tableBorder = theme === 'dark' ? 'border-gray-700' : 'border-gray-200';
  const tableRowHover = theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50';

  if (!userInfo || userInfo.email !== ADMIN_EMAIL) {
    return (
      <div className={`max-w-4xl mx-auto ${bgColor} rounded-xl shadow-lg p-8 mt-10 mb-10 text-center`}>
        <h2 className={`text-2xl font-bold mb-6 text-red-600`}>Access Denied</h2>
        <p className={textColor}>You do not have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className={`max-w-4xl mx-auto ${bgColor} rounded-xl shadow-lg p-8 mt-10 mb-10`}>
      <h2 className={`text-3xl font-bold mb-6 text-center ${theme === 'dark' ? 'text-yellow-400' : 'text-blue-600'}`}>Admin Panel - User Approvals</h2>
      {error && <div className="mb-4 text-red-500 text-center font-semibold">{error}</div>}
      {actionMsg && <div className="mb-4 text-green-500 text-center font-semibold">{actionMsg}</div>}
      {loading ? (
        <div className={`text-center py-10 ${textColor}`}>Loading users...</div>
      ) : (
        <div className="overflow-x-auto">
          <table className={`w-full border-collapse ${tableBorder}`}>
            <thead>
              <tr className={`${tableHeaderBg}`}>
                <th className={`p-3 border-b ${tableBorder} text-left`}>Username</th>
                <th className={`p-3 border-b ${tableBorder} text-left`}>Email</th>
                <th className={`p-3 border-b ${tableBorder} text-center`}>Status</th>
                <th className={`p-3 border-b ${tableBorder} text-center`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user._id} className={`text-center ${tableRowHover} ${tableBorder}`}>
                  <td className={`p-3 border-b ${tableBorder}`}>{user.username}</td>
                  <td className={`p-3 border-b ${tableBorder}`}>{user.email}</td>
                  <td className={`p-3 border-b ${tableBorder}`}>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      user.status === 'pending' ? (theme === 'dark' ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-200 text-yellow-800') :
                      user.status === 'active' ? (theme === 'dark' ? 'bg-green-900 text-green-300' : 'bg-green-200 text-green-800') :
                      'bg-red-200 text-red-800'
                    }`}>
                      {user.status}
                    </span>
                  </td>
                  <td className={`p-3 border-b ${tableBorder}`}>
                    {user.status === "pending" && (
                      <div className="flex justify-center gap-2">
                        <button
                          className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded flex items-center gap-1"
                          onClick={() => handleAction(user._id, "approve")}
                        >
                          <Check size={16} /> Approve
                        </button>
                        <button
                          className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded flex items-center gap-1"
                          onClick={() => handleAction(user._id, "reject")}
                        >
                          <X size={16} /> Reject
                        </button>
                      </div>
                    )}
                    {user.status !== "pending" && <span className="text-gray-400">-</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;